import { AnimatePresence, motion } from 'framer-motion';
import { useState, useEffect } from 'react';

import {
  CloseIcon,
  ErrorIcon,
  InfoIcon,
  SuccessIcon,
  WarningIcon,
} from './components';
import styles from './Disclaimer.module.scss';

export interface DisclaimerProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  title: string;
  body?: string;
  buttons?: React.ReactNode;
  closable?: boolean;
  type?: 'info' | 'warning' | 'error' | 'success';
  visible?: boolean;
  onClose?: () => void;
}

export const Disclaimer = ({
  title,
  body,
  buttons,
  closable = true,
  type = 'info',
  visible,
  onClose,
  ...props
}: DisclaimerProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(visible ?? true);

  useEffect(() => {
    if (visible !== undefined) {
      setIsVisible(visible);
    }
  }, [visible]);

  let icon;
  switch (type) {
    case 'info':
      icon = <InfoIcon />;
      break;
    case 'warning':
      icon = <WarningIcon />;
      break;
    case 'error':
      icon = <ErrorIcon />;
      break;
    case 'success':
      icon = <SuccessIcon />;
      break;
  }

  const onCloseHandler = () => {
    setIsVisible(false);
    onClose?.();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            {...props}
            className={styles.disclaimer}
            data-type={type}
            data-test-id="disclaimer-container"
          >
            <div
              className={styles.icon}
              data-test-id={`disclaimer-icon-${type}`}
            >
              {icon}
            </div>
            <div className={styles.content}>
              <div className={styles.title} data-test-id="disclaimer-title">
                {title}
              </div>
              {body && (
                <div className={styles.body} data-test-id="disclaimer-body">
                  {body}
                </div>
              )}
              {buttons && (
                <div
                  className={styles.buttons}
                  data-test-id="disclaimer-buttons"
                >
                  {buttons}
                </div>
              )}
            </div>
            {closable && (
              <div
                className={styles.closeIcon}
                data-test-id="disclaimer-close-button"
                onClick={onCloseHandler}
              >
                <CloseIcon />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
